package data.objects.views.directediting

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.ObjectVisibility
import apiclient.tags.getTagValues
import apiclient.tags.getUniqueTag
import apiclient.validations.parseEnumValue
import auth.ApiUserStore
import auth.Features
import data.objects.ActiveObjectStore
import dev.fritz2.core.id
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.withKoin
import localization.TL
import localization.getTranslationFlow
import mainmenu.ActiveSearchMenuStore
import mainmenu.RouterStore
import mainmenu.SearchMenu
import mapsearch.historyPathTool.ObjectAndResults
import model.isInternalAdminGroupAdminOrGroupOwner
import objectrouting.NavigationStore
import objectrouting.NavigationToolToggleStore
import qrcode.ManualEnterStore
import qrcode.ScannedCodeStore
import qrcode.ScannedCodeTypeStore
import search.global.SearchSelectedObjectStore
import theme.FormationIcons
import theme.FormationUIIcons
import twcomponents.Delete
import twcomponents.deleteObjectModal
import twcomponents.doIfFeatureFlagEnabled
import utils.ToggleStore
import utils.getCase
import utils.getName
import workspacetools.usermanagement.confirm

/**
 * Definition of the ObjectActionButtons
 */

fun actionButtons(obj: GeoObjectDetails, history: Map<String, ObjectAndResults>): List<ActionBtn> {
    withKoin {
        val apiUserStore: ApiUserStore = get()
        val activeObjectStore: ActiveObjectStore = get()
        val routerStore: RouterStore = get()
        val objType = obj.objectType
        val isAnonymous = apiUserStore.current.isAnonymous
        val archived = obj.tags.getUniqueTag(ObjectTags.Archived).toBoolean()
        val flagged = obj.tags.getUniqueTag(ObjectTags.Flagged).toBoolean()
        val allowedToArchive = obj.canModify || obj.canManage
        val allowedToUnflag = apiUserStore.current.isInternalAdminGroupAdminOrGroupOwner()
        val allowToDelete = obj.canManage
        val allowedToHideMarker = obj.canModify || obj.canManage || apiUserStore.current.isInternalAdminGroupAdminOrGroupOwner()
        val hasExternalId = obj.tags.getTagValues(ObjectTags.ExternalId).isNotEmpty()
        val visibility = parseEnumValue<ObjectVisibility>(obj.tags.getUniqueTag(ObjectTags.Visibility))

        return listOfNotNull(
            // UPDATE POSITION
            if (obj.editable()) {
                ActionBtn(
                    icon = FormationIcons.Position,
                    title = TL.CardSecondaryMenu.UPDATE_POSITION_SHORT,
                    tooltip = TL.CardSecondaryMenu.UPDATE_POSITION,
                    tooltipArgs = mapOf(
                        "case" to objType.getCase(),
                        "objectType" to obj.objectType.getName(),
                    ),
                    tooltipSub = TL.CardSecondaryMenu.UPDATE_POSITION_SUBTITLE,
                    tooltipPlacement = PlacementValues.topStart,
                ) {
                    clicks handledBy {
                        activeObjectStore.focusThisObject(Unit)
                        activeObjectStore.removeMapClickListener(Unit)
                        activeObjectStore.hideActiveObjectMarker(Unit)
                        routerStore.addOrReplaceRoute(mapOf("edit" to obj.objectType.name, "change" to "badge"))
                    }
                }
            } else null,
            // DIRECTIONS
            Features.EnableRouteToObject.doIfFeatureFlagEnabled {
                ActionBtn(
                    icon = FormationUIIcons.Directions,
                    title = TL.CardSecondaryMenu.DIRECTIONS,
                    tooltip = TL.CardSecondaryMenu.DIRECTIONS,
                    tooltipSub = TL.CardSecondaryMenu.DIRECTIONS_SUBTITLE,
                    tooltipSubArgs = mapOf(
                        "case" to objType.getCase(),
                        "objectType" to obj.objectType.getName(),
                    ),
                ) {
                    withKoin {
                        val searchSelectedObjectStore: SearchSelectedObjectStore = get()
                        val navigationToolToggleStore: NavigationToolToggleStore = get()
                        val navigationStore: NavigationStore = get()
                        val activeSearchMenuStore: ActiveSearchMenuStore = get()

                        clicks handledBy {
                            navigationToolToggleStore.update(true)
                            searchSelectedObjectStore.current?.let { obj ->
                                navigationStore.setDestinationObjectWithSourceAndLatLon(obj)
                            }
                            activeSearchMenuStore.update(SearchMenu.Navigation)
                        }
                    }
                }
            },
            // CONNECT TO QR CODE
            if (obj.editable()) {
                if (hasExternalId) {
                    when (objType) {
                        ObjectType.ObjectMarker -> {
                            // Release TrackedObject from QR code and archive object (only for tracked objects)
                            ActionBtn(
                                icon = FormationIcons.QRCode,
                                title = TL.CardSecondaryMenu.RELEASE_AND_ARCHIVE_CODE_SHORT,
                                tooltip = TL.CardSecondaryMenu.RELEASE_AND_ARCHIVE_CODE,
                                tooltipSub = TL.CardSecondaryMenu.RELEASE_AND_ARCHIVE_CODE_SUBTITLE,
                                tooltipSubArgs = mapOf("objectType" to obj.objectType.getName()),
                            ) {
                                clicks handledBy confirm(
                                    TL.CardSecondaryMenu.RELEASE_AND_ARCHIVE_CODE_ARE_YOU_SURE.getTranslationFlow(
                                        mapOf(
                                            "objectType" to obj.objectType.getName(),
                                        ),
                                    ),
                                    listOf(activeObjectStore.releaseTrackedObject),
                                )
                            }
                        }
                        // Remove QR code from object
                        ObjectType.POI, ObjectType.Task, ObjectType.Event, ObjectType.Zone -> {
                            ActionBtn(
                                icon = FormationIcons.QRCode,
                                title = TL.CardSecondaryMenu.REMOVE_CODE_SHORT,
                                tooltip = TL.CardSecondaryMenu.REMOVE_CODE,
                                tooltipSub = TL.CardSecondaryMenu.REMOVE_CODE_SUBTITLE,
                                tooltipSubArgs = mapOf(
                                    "case" to objType.getCase(),
                                    "objectType" to obj.objectType.getName(),
                                ),
                            ) {
                                clicks handledBy confirm(
                                    TL.CardSecondaryMenu.REMOVE_CODE_ARE_YOU_SURE.getTranslationFlow(
                                        mapOf(
                                            "objectType" to obj.objectType.getName(),
                                        ),
                                    ),
                                    listOf(activeObjectStore.removeQRCodeFromObject),
                                )
                            }
                        }

                        else -> {
                            null
                        }
                    }
                } else { // Connect object with QR code
                    when (objType) {
                        ObjectType.ObjectMarker, ObjectType.POI, ObjectType.Task, ObjectType.Event, ObjectType.Zone -> {
                            ActionBtn(
                                icon = FormationIcons.QRCode,
                                title = TL.CardSecondaryMenu.CONNECT_TO_QR_CODE_SHORT,
                                tooltip = TL.CardSecondaryMenu.CONNECT_TO_QR_CODE,
                                tooltipSub = TL.CardSecondaryMenu.CONNECT_TO_QR_CODE_SUBTITLE,
                                tooltipSubArgs = mapOf(
                                    "case" to objType.getCase(),
                                    "objectType" to obj.objectType.getName(),
                                ),
                            ) {
                                withKoin {
                                    val scannedCodeStore: ScannedCodeStore = get()
                                    val scannedCodeTypeStore: ScannedCodeTypeStore = get()
                                    val manualEnterStore: ManualEnterStore = get()
                                    clicks handledBy {
                                        // TODO turn this connect QR action into a modal
                                        routerStore.addOrReplaceRoute(mapOf("show" to "connectQR"))
                                        scannedCodeStore.reset(Unit)
                                        scannedCodeTypeStore.reset(Unit)
                                        manualEnterStore.reset(Unit)
                                    }
                                }
                            }
                        }

                        else -> {
                            null
                        }
                    }
                }
            } else null,
            // HISTORY
            if (!history[obj.id]?.results.isNullOrEmpty()) {
                ActionBtn(
                    icon = FormationIcons.History,
                    title = TL.CardSecondaryMenu.OBJECT_HISTORY_SHORT,
                    tooltip = TL.CardSecondaryMenu.OBJECT_HISTORY,
                    tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                    tooltipSub = TL.CardSecondaryMenu.OBJECT_HISTORY_SUBTITLE,
                    tooltipSubArgs = mapOf(
                        "case" to objType.getCase(),
                        "objectType" to objType.getName(),
                    ),
                ) {
                    clicks handledBy {
                        routerStore.addOrReplaceRoute(mapOf("show" to "objectHistory"))
                    }
                }
            } else null,
            // DUPLICATE
            if (!isAnonymous) {
                ActionBtn(
                    icon = FormationUIIcons.Copy,
                    title = TL.CardSecondaryMenu.DUPLICATE,
                    tooltip = TL.CardSecondaryMenu.DUPLICATE,
                    tooltipSub = TL.CardSecondaryMenu.DUPLICATE_THIS,
                    tooltipSubArgs = mapOf(
                        "case" to objType.getCase(),
                        "objectType" to objType.getName(),
                    ),
                ) {
                    clicks handledBy activeObjectStore.duplicate
                }
            } else null,
            // ARCHIVE
            if (!archived && allowedToArchive) {
                ActionBtn(
                    icon = FormationIcons.Archive,
                    title = TL.CardSecondaryMenu.ARCHIVE,
                    tooltip = TL.CardSecondaryMenu.ARCHIVE,
                    tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                    tooltipSub = TL.CardSecondaryMenu.ARCHIVE_SUBTITLE,
                    tooltipSubArgs = mapOf("objectType" to obj.objectType.getName()),
                ) {
                    clicks handledBy confirm(
                        TL.CardSecondaryMenu.ARCHIVE_ARE_YOU_SURE.getTranslationFlow(
                            mapOf(
                                "case" to objType.getCase(),
                                "objectType" to obj.objectType.getName(),
                            ),
                        ),
                        listOf(activeObjectStore.archiveObject),
                    )
                }
            } else null,
            // RESTORE
            if (archived && allowedToArchive) {
                ActionBtn(
                    icon = FormationIcons.UnArchive,
                    title = TL.CardSecondaryMenu.UNARCHIVE_SHORT,
                    tooltip = TL.CardSecondaryMenu.UNARCHIVE,
                    tooltipSub = TL.CardSecondaryMenu.UNARCHIVE_SUBTITLE,
                    tooltipSubArgs = mapOf("objectType" to obj.objectType.getName()),
                ) {
                    clicks handledBy confirm(
                        TL.CardSecondaryMenu.UNARCHIVE_ARE_YOU_SURE.getTranslationFlow(
                            mapOf(
                                "case" to objType.getCase(),
                                "objectType" to obj.objectType.getName(),
                            ),
                        ),
                        listOf(activeObjectStore.unArchiveObject),
                    )
                }
            } else null,
            // MARKER VISIBILITY
            if (allowedToHideMarker) {
                when (visibility) {
                    ObjectVisibility.MarkerHidden -> {
                        // Show Marker on Map
                        ActionBtn(
                            icon = FormationUIIcons.Eye,
                            title = TL.CardSecondaryMenu.SHOW_MARKER_SHORT,
                            tooltip = TL.CardSecondaryMenu.SHOW_MARKER,
                            tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                            tooltipSub = TL.CardSecondaryMenu.SHOW_MARKER_SUBTITLE,
                        ) {
                            clicks handledBy activeObjectStore.showObjectMarker
                        }
                    }

                    else -> {
                        // Hide Marker on Map
                        ActionBtn(
                            icon = FormationUIIcons.EyeOff,
                            title = TL.CardSecondaryMenu.HIDE_MARKER_SHORT,
                            tooltip = TL.CardSecondaryMenu.HIDE_MARKER,
                            tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                            tooltipSub = TL.CardSecondaryMenu.HIDE_MARKER_SUBTITLE,
                        ) {
                            clicks handledBy activeObjectStore.hideObjectMarker
                        }
                    }
                }
            } else null,
            // FLAG
            Features.EnablePublicSignup.doIfFeatureFlagEnabled {
                if (!flagged && objType != ObjectType.Building) {
                    ActionBtn(
                        icon = FormationIcons.Caution,
                        title = TL.CardSecondaryMenu.FLAG_SHORT,
                        tooltip = TL.CardSecondaryMenu.FLAG,
                        tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                        tooltipSub = TL.CardSecondaryMenu.FLAG_SUBTITLE,
                    ) {
                        clicks handledBy confirm(
                            TL.CardSecondaryMenu.FLAG_ARE_YOU_SURE.getTranslationFlow(
                                mapOf(
                                    "case" to objType.getCase(),
                                    "objectType" to obj.objectType.getName(),
                                ),
                            ),
                            listOf(activeObjectStore.flagObject),
                        )
                    }
                } else null
            },
            // UNFLAG
            if (flagged && allowedToUnflag && objType != ObjectType.Building) {
                ActionBtn(
                    icon = FormationIcons.Caution,
                    title = TL.CardSecondaryMenu.UN_FLAG_SHORT,
                    tooltip = TL.CardSecondaryMenu.UN_FLAG,
                    tooltipArgs = mapOf("objectType" to obj.objectType.getName()),
                    tooltipSub = TL.CardSecondaryMenu.UN_FLAG_SUBTITLE,
                ) {
                    clicks handledBy confirm(
                        TL.CardSecondaryMenu.UN_FLAG_ARE_YOU_SURE.getTranslationFlow(
                            mapOf(
                                "case" to objType.getCase(),
                                "objectType" to obj.objectType.getName(),
                            ),
                        ),
                        listOf(activeObjectStore.unflagObject),
                    )
                }
            } else null,
            // DETAILS
            ActionBtn(
                icon = FormationIcons.Description,
                title = TL.CardSecondaryMenu.DETAILS,
                tooltip = TL.CardSecondaryMenu.DETAILS,
                tooltipSub = TL.CardSecondaryMenu.SHOW_DETAILS,
                tooltipSubArgs = mapOf(
                    "case" to objType.getCase(),
                    "objectType" to objType.getName(),
                ),
                tooltipPlacement = PlacementValues.topEnd,
            ) {
                id("detailsButton")
                clicks handledBy {
                    routerStore.addOrReplaceRoute(mapOf("show" to "details"))
                }
            },
            // DELETE OBJECT
            if (allowToDelete) {
                ActionBtn(
                    icon = FormationIcons.DeleteAlt,
                    title = TL.CardSecondaryMenu.DELETE_SHORT,
                    tooltip = TL.CardSecondaryMenu.DELETE,
                    tooltipArgs = mapOf(
                        "case" to objType.getCase(),
                        "objectType" to objType.getName(),
                    ),
                    tooltipSub = TL.CardSecondaryMenu.DELETE_SUBTITLE,
                    tooltipPlacement = PlacementValues.topEnd,
                    specialClasses = "hover:text-red-500",
                ) {
                    id("deleteButton")
                    val deleteObjectToggle = ToggleStore(false)
                    deleteObjectModal(
                        type = Delete.Object,
                        toggleStore = deleteObjectToggle,
                    )
                    clicks handledBy deleteObjectToggle.toggle
                }
            } else null,
        )
    }
}
